import React, { useState, useEffect } from 'react';
import VendorSideMenuBar from './VendorLayouts/VendorSideMenuBar';
import VendorTopMenuBar from './VendorLayouts/VendorTopMenuBar';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    CircularProgress,
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TableSortLabel,
    TablePagination,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axiosVendorInstance from '../../axiosVendorInstance';
import '../../VendorStyles/CloverConnect.css';
import CloverConnectHelp from './CloverConnectHelp';

function CloverConnect() {
    const [itemCategories, setItemCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [vendorID, setVendorID] = useState(null);
    const [merchantId, setMerchantId] = useState('');
    const [token, setToken] = useState('');
    const [tenderId, setTenderId] = useState('');
    const [formErrors, setFormErrors] = useState({});
    const [isConnected, setIsConnected] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [autoConnectLoading, setAutoConnectLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [availableFilter, setAvailableFilter] = useState('all'); // all | yes | no
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchVendorIDAndCheckCredentials = async () => {
            try {
                const response = await axiosVendorInstance.get('/vendors/account');
                const apiResponse = response?.data?.getVendorInfo;
                console.log('apiResponse', apiResponse);
                const vendorId = apiResponse[0]?.ID;
                setVendorID(vendorId);

                // Check if vendor already has clover credentials
                try {
                    const cloverResponse = await axios.get(`https://pro-customers-api.omyfood.io/api/cartdetails/clover/itemscategories/${vendorId}`);
                    const cloverData = cloverResponse?.data;
                    
                    // If we get a successful response with clover_connected: true, it means credentials exist
                    if (cloverData && cloverData.clover_connected === true) {
                        // Set the merchantId and token from the response if available
                        if (cloverData.merchant_id) {
                            setMerchantId(cloverData.merchant_id);
                        }
                        if (cloverData.token) {
                            setToken(cloverData.token);
                        }
                        
                        // Set the item categories from the response
                        if (cloverData.syncedItems && cloverData.syncedItems.length > 0) {
                            setItemCategories(cloverData.syncedItems);
                            setIsConnected(true);
                        }
                        if (cloverData.tender_id) {
                            setTenderId(cloverData.tender_id);
                        }
                    }
                } catch (cloverError) {
                    console.log('No existing clover credentials found or error fetching them:', cloverError);
                    // This is normal - vendor doesn't have credentials yet
                }
            } catch (error) {
                console.error('Error fetching vendor id:', error);
            } finally {
                setAutoConnectLoading(false);
            }
        };
        fetchVendorIDAndCheckCredentials();
    }, []);

    const handleInputChange = (field, value) => {
        if (field === 'merchantId') {
            setMerchantId(value);
        } else if (field === 'token') {
            setToken(value);
        } else if (field === 'tenderId') {
            setTenderId(value);
        }
        
        // Clear error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!merchantId.trim()) {
            errors.merchantId = 'Merchant ID is required';
        }
        
        if (!token.trim()) {
            errors.token = 'Token is required';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const fetchItemCategories = async (isAutoConnect = false) => {
        if (!isAutoConnect && !validateForm()) {
            return;
        }

        setLoading(true);
        setIsConnected(false);
        try {
            const tenderQuery = tenderId ? `&tender_id=${encodeURIComponent(tenderId)}` : '';
            const response = await axios.get(`https://pro-customers-api.omyfood.io/api/cartdetails/clover/itemscategories/${vendorID}?merchant_id=${encodeURIComponent(merchantId)}&access_token=${encodeURIComponent(token)}${tenderQuery}`);
            
            // Check if clover_connected is true in the response
            const cloverConnected = response.data?.clover_connected;
            
            if (cloverConnected) {
                // Extract items from the new response structure with syncedItems array
                const items = response.data?.syncedItems || [];
                setItemCategories(items);
                setIsConnected(true);
                
                if (!isAutoConnect) {
                    toast.success('Successfully connected to Clover');
                }
                console.log('Item Categories:', items);
            } else {
                // If clover_connected is false but we have items, still show them
                const items = response.data?.syncedItems || [];
                if (items.length > 0) {
                    setItemCategories(items);
                    setIsConnected(true);
                    if (!isAutoConnect) {
                        toast.success('Successfully connected to Clover');
                    }
                } else {
                    toast.error('Connection failed. Please check your credentials.');
                }
            }
        } catch (error) {
            console.error('Error fetching item categories:', error);
            if (error.response) {
                if (error.response.status === 401) {
                    toast.error('Invalid Merchant ID or Token');
                } else if (error.response.status === 404) {
                    toast.error('Merchant not found');
                } else {
                    toast.error('Error connecting to Clover. Please check your credentials.');
                }
            } else {
                toast.error('Network error. Please check your connection.');
            }
            setItemCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const handleConnect = () => {
        fetchItemCategories();
    };

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const getComparable = (item, key) => {
        switch (key) {
        case 'id':
            return String(item.id || '');
        case 'name':
            return String(item.name || '');
        case 'price':
            return Number(item.price_cents ?? 0);
        case 'category':
            return item.categories && item.categories.length > 0
                ? item.categories.map((c) => c.name).join(', ').toLowerCase()
                : '';
        case 'available':
            return item.available ? 1 : 0;
        default:
            return '';
        }
    };

    const filteredItems = React.useMemo(() => {
        const text = searchText.trim().toLowerCase();
        return itemCategories.filter((item) => {
            // Availability filter
            if (availableFilter === 'yes' && !item.available) return false;
            if (availableFilter === 'no' && item.available) return false;

            if (!text) return true;

            const idStr = String(item.id || '').toLowerCase();
            const nameStr = String(item.name || '').toLowerCase();
            const categoryStr = item.categories && item.categories.length > 0
                ? item.categories.map((c) => c.name).join(', ').toLowerCase()
                : '';
            const priceStr = ((item.price_cents ?? 0) / 100).toFixed(2);

            return (
                idStr.includes(text) ||
                nameStr.includes(text) ||
                categoryStr.includes(text) ||
                priceStr.includes(text)
            );
        });
    }, [itemCategories, searchText, availableFilter]);

    const sortedItems = React.useMemo(() => {
        if (!sortConfig.key) return filteredItems;
        const sorted = [...filteredItems].sort((a, b) => {
            const va = getComparable(a, sortConfig.key);
            const vb = getComparable(b, sortConfig.key);
            if (va < vb) return -1;
            if (va > vb) return 1;
            return 0;
        });
        return sortConfig.direction === 'asc' ? sorted : sorted.reverse();
    }, [filteredItems, sortConfig]);

    // Clamp or reset page when filtered/sorted results change
    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(sortedItems.length / rowsPerPage) - 1);
        if (page > maxPage) {
            setPage(0);
        }
    }, [sortedItems.length, rowsPerPage]);

    const paginatedItems = React.useMemo(() => {
        const start = page * rowsPerPage;
        return sortedItems.slice(start, start + rowsPerPage);
    }, [sortedItems, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <VendorSideMenuBar />
            <VendorTopMenuBar />
            <div className="menu-main-container-vendor" style={{ padding: '20px' }}>
                <h2>Clover Connect</h2>
                
                {/* Conditional Rendering of Connection Form */}
                {autoConnectLoading ? (
                    <div className="clover-connect-container" style={{ textAlign: 'center' }}>
                        <CircularProgress />
                        <p>Checking for existing credentials...</p>
                    </div>
                ) : !isConnected ? (
                    <div className="clover-connect-container">
                        <div className="clover-form-header">
                            <h3>Connect to Clover</h3>
                            <p>Enter your Clover Merchant ID and Token to fetch menu items</p>
                        </div>
                        
                        <div className="clover-form-group">
                            <label htmlFor="merchantId">Merchant ID</label>
                            <input
                                type="text"
                                id="merchantId"
                                value={merchantId}
                                onChange={(e) => handleInputChange('merchantId', e.target.value)}
                                placeholder="Enter your Clover Merchant ID"
                                className={formErrors.merchantId ? 'error' : ''}
                                disabled={loading}
                            />
                            {formErrors.merchantId && (
                                <span className="clover-error-message">{formErrors.merchantId}</span>
                            )}
                        </div>
                        
                        <div className="clover-form-group">
                            <label htmlFor="tenderId">Tender ID</label>
                            <input
                                type="text"
                                id="tenderId"
                                value={tenderId}
                                onChange={(e) => handleInputChange('tenderId', e.target.value)}
                                placeholder="Enter Clover Tender ID"
                                className={formErrors.tenderId ? 'error' : ''}
                                disabled={loading}
                            />
                            {formErrors.tenderId && (
                                <span className="clover-error-message">{formErrors.tenderId}</span>
                            )}
                        </div>

                        <div className="clover-form-group">
                            <label htmlFor="token">Token</label>
                            <input
                                type="password"
                                id="token"
                                value={token}
                                onChange={(e) => handleInputChange('token', e.target.value)}
                                placeholder="Enter your Clover API Token"
                                className={formErrors.token ? 'error' : ''}
                                disabled={loading}
                            />
                            {formErrors.token && (
                                <span className="clover-error-message">{formErrors.token}</span>
                            )}
                        </div>
                        
                        <button
                            className="clover-connect-button"
                            onClick={handleConnect}
                            disabled={loading}
                        >
                            {loading ? 'Connecting...' : 'Connect to Clover'}
                        </button>

                        {/* Help Button */}
                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            <button 
                                onClick={() => setHelpOpen(true)}
                                className="clover-help-button"
                            >
                                Need help finding your Merchant ID and Token & Tender Id?
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="clover-connection-status success">
                        ✅ Successfully connected to Clover
                    </div>
                )}

                {/* Help Modal */}
                <CloverConnectHelp 
                    open={helpOpen} 
                    handleClose={() => setHelpOpen(false)} 
                />

                {/* Results Section */}
                {itemCategories.length > 0 && (
                    <div style={{ marginTop: '30px' }}>
                        <h3>Clover Menu Items</h3>
                        {/* Filters */}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                            <TextField
                                label="Search"
                                size="small"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Search by ID, name, category, price"
                            />
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel id="available-filter-label">Available</InputLabel>
                                <Select
                                    labelId="available-filter-label"
                                    label="Available"
                                    value={availableFilter}
                                    onChange={(e) => setAvailableFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="yes">Yes</MenuItem>
                                    <MenuItem value="no">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                                <CircularProgress />
                            </div>
                        ) : (
                            <>
                                <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ color: 'red', fontWeight: 'bold' }}>
                                                    <TableSortLabel
                                                        active={sortConfig.key === 'id'}
                                                        direction={sortConfig.key === 'id' ? sortConfig.direction : 'asc'}
                                                        onClick={() => handleSort('id')}
                                                    >
                                                        ID
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell sx={{ color: 'red', fontWeight: 'bold' }}>
                                                    <TableSortLabel
                                                        active={sortConfig.key === 'name'}
                                                        direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                                                        onClick={() => handleSort('name')}
                                                    >
                                                        Name
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell sx={{ color: 'red', fontWeight: 'bold' }}>
                                                    <TableSortLabel
                                                        active={sortConfig.key === 'price'}
                                                        direction={sortConfig.key === 'price' ? sortConfig.direction : 'asc'}
                                                        onClick={() => handleSort('price')}
                                                    >
                                                        Price
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell sx={{ color: 'red', fontWeight: 'bold' }}>
                                                    <TableSortLabel
                                                        active={sortConfig.key === 'category'}
                                                        direction={sortConfig.key === 'category' ? sortConfig.direction : 'asc'}
                                                        onClick={() => handleSort('category')}
                                                    >
                                                        Category Name
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell sx={{ color: 'red', fontWeight: 'bold' }}>
                                                    <TableSortLabel
                                                        active={sortConfig.key === 'available'}
                                                        direction={sortConfig.key === 'available' ? sortConfig.direction : 'asc'}
                                                        onClick={() => handleSort('available')}
                                                    >
                                                        Available
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell sx={{ color: 'red', fontWeight: 'bold' }}>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedItems.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell>{item.id}</TableCell>
                                                    <TableCell>{item.name}</TableCell>
                                                    <TableCell>{(item.price_cents / 100).toFixed(2)}</TableCell>
                                                    <TableCell>
                                                        {item.categories && item.categories.length > 0
                                                            ? item.categories.map(cat => cat.name).join(', ')
                                                            : '-'}
                                                    </TableCell>
                                                    <TableCell style={{ textAlign: 'center' }}>
                                                        {item.available ? 'Yes' : 'No'}
                                                    </TableCell>
                                                    <TableCell style={{ textAlign: 'center' }}>
                                                        {item.synced_to_menu ? (
                                                            <button className="menu-add-item-button" disabled>
                                                                Item Added
                                                            </button>
                                                        ) : (
                                                            <button
                                                                className="menu-add-item-button"
                                                                onClick={() => {
                                                                    navigate('/menu', { state: { itemData: item } });
                                                                }}
                                                            >
                                                                Add
                                                            </button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    component="div"
                                    count={sortedItems.length}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                />
                            </>
                        )}
                    </div>
                )}
                
                {!loading && itemCategories.length === 0 && isConnected && (
                    <p style={{ marginTop: '20px', textAlign: 'center' }}>No item categories found.</p>
                )}
            </div>
        </>
    );
}

export default CloverConnect;
